import { useEffect, useState } from 'react';

import { fetchExecutionRequestStatus } from '../../../services/workflowService';

const DEFAULT_POLL_INTERVAL_MS = 1500;
const TERMINAL_REQUEST_STATUSES = new Set(['completed', 'failed']);

function normalizePollInterval(intervalMs) {
  return Number.isFinite(intervalMs) && intervalMs >= 0
    ? intervalMs
    : DEFAULT_POLL_INTERVAL_MS;
}

export function useExecutionRequestPolling(
  executionRequestId,
  { intervalMs = DEFAULT_POLL_INTERVAL_MS } = {}
) {
  const [executionRequest, setExecutionRequest] = useState(null);
  const [pollingError, setPollingError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const requestId = executionRequestId ? String(executionRequestId) : null;
  const pollIntervalMs = normalizePollInterval(intervalMs);

  useEffect(() => {
    let active = true;
    let continuePolling = Boolean(requestId);
    let timerId = null;

    if (!requestId) {
      setExecutionRequest(null);
      setPollingError(null);
      setIsPolling(false);
      return () => {
        active = false;
      };
    }

    setExecutionRequest(null);
    setPollingError(null);
    setIsPolling(true);

    const poll = async () => {
      try {
        const nextExecutionRequest = await fetchExecutionRequestStatus(requestId);
        if (!active) return;

        setExecutionRequest(nextExecutionRequest);
        setPollingError(null);

        const requestStatus = String(
          nextExecutionRequest?.status || ''
        ).toLowerCase();
        if (TERMINAL_REQUEST_STATUSES.has(requestStatus)) {
          continuePolling = false;
          setIsPolling(false);
        }
      } catch (error) {
        if (!active) return;
        setPollingError(error);
      }

      if (active && continuePolling) {
        timerId = window.setTimeout(poll, pollIntervalMs);
      }
    };

    void poll();

    return () => {
      active = false;
      continuePolling = false;
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, [pollIntervalMs, requestId]);

  return {
    executionRequest,
    pollingError,
    isPolling,
  };
}

export { DEFAULT_POLL_INTERVAL_MS };
